import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { gungComparatorHelper } from 'gung-common';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { EdiPricat } from '../../models/edi';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';
import { EdiService } from '../edi/edi.service';
import { EdiMinDateConfigService } from '../edi-min-date-config.service';

export class EdiPricatStatusFilter extends SimpleConfigBaseFilter<EdiPricat> {
  constructor() {
    super();
  }
  getName(): string {
    return 'PRICAT_STATUS';
  }
  getOptionIds(item: EdiPricat): string[] {
    return [item.gungEdiMessageSendStatus || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiPricatValidationFilter extends SimpleConfigBaseFilter<EdiPricat> {
  constructor() {
    super();
  }
  getName(): string {
    return 'VALIDATION_STATE';
  }
  getOptionIds(item: EdiPricat): string[] {
    return [item.validationState || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiPricatConfigIdFilter extends SimpleConfigBaseFilter<EdiPricat> {
  constructor() {
    super();
  }
  getName(): string {
    return 'CONFIG_ID';
  }
  getOptionIds(item: EdiPricat): string[] {
    return [item.ediConfigId || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiPricatDateFilter extends SimpleConfigBaseFilter<EdiPricat> {
  constructor() {
    super();
  }
  getName(): string {
    return 'DATE';
  }
  getOptionIds(item: EdiPricat): string[] {
    const date = item.timestamp ? new Date(item.timestamp) : new Date();
    return [format(date, 'yyyy-MM-dd')];
  }
  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EdiPricatListConfigService implements EdiMinDateConfigService<EdiPricat> {
  protected minDateSubject = new BehaviorSubject<string>(this.getDefaultMinDate());

  constructor(protected ediService: EdiService, protected baseViewConfigService: BaseViewConfigService) {}

  getDefaultMinDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return format(date, 'yyyy-MM-dd');
  }

  setMinDate(minDate: string): void {
    this.minDateSubject.next(minDate);
  }

  getMinDate(): string {
    return this.minDateSubject.getValue();
  }

  getItems(): Observable<EdiPricat[]> {
    return this.minDateSubject.pipe(
      switchMap(minDate => this.ediService.getPricatMessages(minDate)),
      map(items => items.sort((a, b) => gungComparatorHelper([a.timestamp, a.id], [b.timestamp, b.id], -1)))
    );
  }

  getFilters(): ConfigBaseFilter<EdiPricat>[] {
    return [
      new EdiPricatValidationFilter(),
      new EdiPricatStatusFilter(),
      new EdiPricatConfigIdFilter(),
      new EdiPricatDateFilter()
    ];
  }

  getSortOptions(): ListSortOption<EdiPricat>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<EdiPricat>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => this.baseViewConfigService.getEdiPricatCardListComponent(),
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'EDI PRICAT grid'
      }
    ];
  }

  getSearchTerms(item: EdiPricat): string[] {
    return [item.id, item.ediConfigId, item.buyerGln?.toString()].filter(s => !!s);
  }

  getItemId(item: EdiPricat): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
